import CONFIG from '../../globalVariables.js';

class CategoriesService {
  // **HELPER - Résoudre l'URL d'image (uploads -> API absolu)**
  static resolveImageUrl(url) {
    if (!url) return '';
    const u = String(url);
    // Si l'URL absolue pointe vers localhost et contient /uploads, on la réécrit vers PATH_API
    if (u.startsWith('http://') || u.startsWith('https://')) {
      try {
        const parsed = new URL(u);
        const isLocalHost = /^(localhost|127\.0\.0\.1)$/i.test(parsed.hostname);
        const isUploadPath = parsed.pathname.startsWith('/uploads/');
        if (isLocalHost && isUploadPath) {
          // Réécrire pour utiliser l'API configurée (port/path dynamiques)
          return `${CONFIG.PATH_API}${parsed.pathname}`;
        }
      } catch (e) {
        // En cas d'erreur de parsing, on continue le flux normal
      }
      return u;
    }
    if (u.startsWith('/images/')) return u; // assets frontend
    if (u.startsWith('/uploads/')) return `${CONFIG.PATH_API}${u}`; // chemin absolu côté API
    if (u.includes('/uploads/')) {
      const norm = u.startsWith('/') ? u : `/${u}`;
      return `${CONFIG.PATH_API}${norm}`;
    }
    // Probable nom de fichier simple provenant d'un upload
    return `${CONFIG.PATH_API}/uploads/${u.replace(/^\//, '')}`;
  }
  
  // **GET - Récupérer toutes les catégories**
  static async getAllCategories() {
    try {
      console.log('🔄 Tentative d\'appel API:', `${CONFIG.PATH_API}/categories`);
      console.log('🔧 CONFIG.PATH_API:', CONFIG.PATH_API);
      
      const response = await fetch(`${CONFIG.PATH_API}/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('📡 Response status:', response.status, response.statusText);
      console.log('📡 Response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ API Response received:', data.success, 'Count:', data.count);
      
      if (!data.success || !data.data) {
        throw new Error('Format de réponse API invalide');
      }
      
      // Normaliser les URLs d'images pour garantir le chargement côté frontend
      const categories = Array.isArray(data.data) ? data.data.map(c => ({
        ...c,
        image: this.resolveImageUrl(c.image)
      })) : [];
      return categories;
    } catch (error) {
      console.error('🚨 Erreur dans getAllCategories:', error);
      console.error('🚨 Type d\'erreur:', typeof error);
      console.error('🚨 Message d\'erreur:', error.message);
      
      // Fallback vers les données locales en cas d'erreur
      console.warn('🔄 Utilisation du fallback...');
      return this.getFallbackCategories();
    }
  }

  // **GET - Récupérer les catégories par type (particulier/professionnel)**
  static async getCategoriesByType(type) {
    try {
      const response = await fetch(`${CONFIG.PATH_API}/categories/type/${type}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Erreur lors de la récupération des catégories par type');
      }
      const categories = Array.isArray(data.data) ? data.data.map(c => ({
        ...c,
        image: this.resolveImageUrl(c.image)
      })) : [];
      return categories;
    } catch (error) {
      console.error('Erreur API getCategoriesByType:', error);
      const fallbackCategories = this.getFallbackCategories();
      return fallbackCategories.filter(cat => cat.type === type);
    }
  }

  // **GET - Récupérer les catégories par zone (interior/exterior)**
  static async getCategoriesByZone(zone) {
    try {
      const allCategories = await this.getAllCategories();
      return allCategories.filter(cat => cat.zone === zone);
    } catch (error) {
      console.error('Erreur getCategoriesByZone:', error);
      return [];
    }
  }

  // **GET - Récupérer les catégories par type ET zone**
  static async getCategoriesByTypeAndZone(type, zone) {
    try {
      const allCategories = await this.getAllCategories();
      return allCategories.filter(cat => cat.type === type && cat.zone === zone);
    } catch (error) {
      console.error('Erreur getCategoriesByTypeAndZone:', error);
      return [];
    }
  }

  // **GET - Récupérer les catégories organisées pour l'affichage**
  static async getCategoriesForDisplay() {
    try {
      console.log('🔄 getCategoriesForDisplay() - début');
      const allCategories = await this.getAllCategories();
      console.log('📊 Catégories récupérées:', allCategories.length);
      
      // Ne montrer que les catégories visibles (enable === true)
      const visibleCategories = allCategories.filter(cat => cat.enable !== false);

      const result = {
        interior: [],
        exterior: [],
        professional: []
      };

      visibleCategories.forEach(category => {
        if (category.type === 'particulier') {
          if (category.zone === 'interior') {
            result.interior.push(category);
          } else if (category.zone === 'exterior') {
            result.exterior.push(category);
          }
        } else if (category.type === 'professionnel') {
          result.professional.push(category);
        }
      });

      console.log('📊 Résultat organisé:', {
        interior: result.interior.length,
        exterior: result.exterior.length,
        professional: result.professional.length
      });

      return result;
    } catch (error) {
      console.error('🚨 Erreur getCategoriesForDisplay:', error);
      throw error;
    }
  }

  // **POST - Créer une catégorie**
  static async create(payload) {
    try {
      const response = await fetch(`${CONFIG.PATH_API}/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Erreur création catégorie');
      }
      const cat = data.data || {};
      return { ...cat, image: this.resolveImageUrl(cat.image) };
    } catch (error) {
      console.error('🚨 Erreur create catégorie:', error);
      throw error;
    }
  }

  // **PUT - Mettre à jour une catégorie**
  static async update(id, payload) {
    try {
      const response = await fetch(`${CONFIG.PATH_API}/categories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Erreur mise à jour catégorie');
      }
      const cat = data.data || {};
      return { ...cat, image: this.resolveImageUrl(cat.image) };
    } catch (error) {
      console.error('🚨 Erreur update catégorie:', error);
      throw error;
    }
  }

  // **DELETE - Supprimer une catégorie**
  static async remove(id) {
    try {
      const response = await fetch(`${CONFIG.PATH_API}/categories/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Erreur suppression catégorie');
      }
      return data.data;
    } catch (error) {
      console.error('🚨 Erreur remove catégorie:', error);
      throw error;
    }
  }

  // **TRANSFORMATION - Convertir vers le format frontend legacy**
  static transformToLegacyFormat(categories) {
    const result = {
      services: {
        interior: { categories: {} },
        exterior: { categories: {} }
      },
      professional: {
        categories: {}
      }
    };

    categories.forEach(category => {
      const categoryData = {
        title: this.getCategoryTitle(category.nom), // Convertir nom -> titre
        image: category.image, // Utiliser directement l'image de la base de données
        subcategories: category.sous_categories.map(sub => sub.nom)
      };

      if (category.type === 'particulier') {
        if (category.zone === 'interior') {
          result.services.interior.categories[category.nom] = categoryData;
        } else if (category.zone === 'exterior') {
          result.services.exterior.categories[category.nom] = categoryData;
        }
      } else if (category.type === 'professionnel') {
        result.professional.categories[category.nom] = categoryData;
      }
    });

    return result;
  }

  // **HELPER - Convertir nom -> titre pour l'affichage**
  static getCategoryTitle(nom) {
    const titlesMap = {
      'painting': 'Peinture',
      'flooring': 'Sol',
      'plumbing': 'Plomberie',
      'electrical': 'Électricité',
      'partitions': 'Cloisons / Placo',
      'interiorJoinery': 'Menuiserie intérieure',
      'kitchen': 'Cuisine',
      'bathroom': 'Salle de bain',
      'heating': 'Chauffage',
      'interiorInsulation': 'Isolation intérieure',
      'masonry': 'Maçonnerie',
      'facade': 'Façade',
      'roofing': 'Toiture',
      'exteriorJoinery': 'Menuiserie extérieure',
      'terrace': 'Terrasse',
      'fencing': 'Clôtures / Portails',
      'garden': 'Jardin',
      'restaurant': 'Restauration',
      'parking': 'Stationnement',
      'office': 'Bureaux et espaces professionnels',
      'security': 'Sécurité',
      'access': 'Accès et élévation'
    };
    
    return titlesMap[nom] || nom;
  }

  // **FALLBACK - Données locales en cas d'erreur API**
  static getFallbackCategories() {
    console.warn('🔄 Utilisation des données fallback locales');
    
    // Données fallback simples directement dans le service
    return [
      {
        nom: 'Peinture',
        type: 'particulier',
        zone: 'interior',
        image: '/images/categories/peinture.jpg',
        sous_categories: [
          { nom: 'Murs intérieurs' },
          { nom: 'Plafonds' },
          { nom: 'Effets décoratifs' }
        ],
        description: 'Travaux de peinture intérieure'
      },
      {
        nom: 'Sol',
        type: 'particulier',
        zone: 'interior',
        image: '/images/categories/sol.jpg',
        sous_categories: [
          { nom: 'Parquet' },
          { nom: 'Carrelage' },
          { nom: 'Moquette' }
        ],
        description: 'Revêtements de sol'
      },
      {
        nom: 'Cuisine',
        type: 'particulier',
        zone: 'interior',
        image: '/images/categories/cuisine.jpg',
        sous_categories: [
          { nom: 'Montage de cuisine' },
          { nom: 'Plan de travail' },
          { nom: 'Électroménager' }
        ],
        description: 'Aménagement cuisine'
      },
      {
        nom: 'Façade',
        type: 'particulier',
        zone: 'exterior',
        image: '/images/categories/facade.jpg',
        sous_categories: [
          { nom: 'Ravalement' },
          { nom: 'Peinture extérieure' },
          { nom: 'Enduits' }
        ],
        description: 'Ravalement de façade'
      }
    ];
  }

  // **HELPER - Obtenir le format legacy pour le frontend existant**
  static async getLegacyFormat() {
    const categories = await this.getAllCategories();
    return this.transformToLegacyFormat(categories);
  }

  // **HELPER - Obtenir les données métier des sous-catégories**
  static async getSubcategoryBusinessData() {
    try {
      const categories = await this.getAllCategories();
      const businessData = {};
      
      categories.forEach(category => {
        businessData[category.nom] = {};
        category.sous_categories.forEach(subcat => {
          businessData[category.nom][subcat.nom] = {
            surface_piece: subcat.surface_piece,
            surface_elements: subcat.surface_elements,
            multi_pieces: subcat.multi_pieces,
            nombre_elements: subcat.nombre_elements
          };
        });
      });
      
      return businessData;
    } catch (error) {
      console.error('Erreur getSubcategoryBusinessData:', error);
      return {};
    }
  }
}

export default CategoriesService;